import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, List, ListItem, ListItemText, Menu, MenuItem, TextField, Tooltip, Typography,
  Table, TableBody, TableCell, TableHead, TableRow
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import { ListItemIcon } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../services/api";

/*
 * Ligação do contato com o sistema do cliente (Sabe Gestão Web e próximos).
 * SEGURANÇA (card tQJYtrWG): esta tela só chama as rotas /integracao/* do
 * próprio SabeZap. Nunca chama o sistema do cliente direto, nunca recebe
 * chave nem token, e não manda usuário do sistema: o servidor descobre quem é
 * o atendente pela sessão.
 */

const MENSAGENS = {
  ERR_INTEGRACAO_SEM_ACESSO_CLIENTE: "Você não tem acesso a este cliente no sistema.",
  ERR_INTEGRACAO_USUARIO_INVALIDO: "Seu usuário no sistema foi desativado ou trocado. Peça ao administrador para religar o seu usuário.",
  ERR_INTEGRACAO_ATENDENTE_SEM_USUARIO: "Seu usuário do SabeZap ainda não foi ligado a um usuário do sistema. Fale com o administrador.",
  ERR_INTEGRACAO_SEM_PERMISSAO: "Você não tem permissão para esta janela no sistema.",
  ERR_INTEGRACAO_MODULO: "A empresa não usa este módulo no sistema.",
  ERR_INTEGRACAO_SEM_CLIENTES: "Ligue o contato a pelo menos um cliente primeiro.",
  ERR_INTEGRACAO_CONTA_DE_OUTRO_CLIENTE: "Esta conta não é de um cliente ligado a este contato.",
  ERR_INTEGRACAO_CONTRATO_DE_OUTRO_CLIENTE: "Este contrato não é de um cliente ligado a este contato.",
  ERR_INTEGRACAO_BOLETO: "O sistema não gerou o PDF do boleto desta conta.",
  ERR_INTEGRACAO_CONTRATO_PDF: "O sistema não gerou o PDF deste contrato.",
  ERR_INTEGRACAO_INDISPONIVEL: "O sistema do cliente não respondeu. Tente de novo em instantes.",
  ERR_INTEGRACAO_CHAVE_INVALIDA: "A chave de integração foi recusada pelo sistema. Fale com o administrador.",
  ERR_INTEGRACAO_TICKET: "Abra a conversa deste contato para enviar.",
  ERR_INTEGRACAO_SEM_ACESSO_CONTATO: "Você não tem acesso a este contato no SabeZap.",
  ERR_INTEGRACAO_SEM_NOTA: "Esta conta ainda não tem nota fiscal emitida.",
  ERR_INTEGRACAO_NOTA_PDF: "O sistema não gerou o PDF desta nota fiscal.",
  ERR_INTEGRACAO_NAO_ENCONTRADO: "Registro não encontrado no sistema.",
  ERR_INTEGRACAO_DE_OUTRO_CLIENTE: "Este registro não é de um cliente ligado a este contato.",
  ERR_INTEGRACAO_PEDIDO_PDF: "O sistema não gerou o PDF deste pedido.",
  ERR_INTEGRACAO_DESCRICAO: "Escreva a descrição do contato.",
  ERR_INTEGRACAO_TIPO: "Escolha um tipo de contato da lista.",
  ERR_INTEGRACAO_DADOS_INVALIDOS: "O sistema recusou os dados do contato.",
  ERR_INTEGRACAO_SEM_DANFE: "Esta NF-e ainda não foi autorizada: não há DANFE para enviar."
};

const avisarErro = (err) => {
  const cod = err && err.response && err.response.data && err.response.data.error;
  toast.error(MENSAGENS[cod] || "Não foi possível falar com o sistema do cliente.");
};

const copiar = async (texto, ok) => {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(ok);
  } catch (e) {
    toast.info(texto);
  }
};

const valorBR = (v) => {
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : v;
};

function JanelaLigar({ aberto, fechar, contactId, rotulo, clientes, onMudou }) {
  const [sug, setSug] = useState(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setSug(null); setRes(null); setQ("");
    api.get(`/integracao/contato/${contactId}/sugestoes`)
      .then(({ data }) => setSug(data))
      .catch((e) => { setSug([]); avisarErro(e); });
  }, [aberto, contactId]);

  const buscar = async (e) => {
    e.preventDefault();
    if (q.trim().length < 3) return;
    setOcupado(true);
    try { const { data } = await api.get(`/integracao/busca`, { params: { q } }); setRes(data); }
    catch (err) { avisarErro(err); }
    setOcupado(false);
  };

  const ligar = async (p) => {
    setOcupado(true);
    try { const { data } = await api.post(`/integracao/contato/${contactId}/clientes`, { id: p.id }); onMudou(data); toast.success(`Ligado a ${p.nome}`); }
    catch (err) { avisarErro(err); }
    setOcupado(false);
  };

  const desligar = async (c) => {
    setOcupado(true);
    try { const { data } = await api.delete(`/integracao/contato/${contactId}/clientes/${c.id}`); onMudou(data); }
    catch (err) { avisarErro(err); }
    setOcupado(false);
  };

  const ligados = new Set(clientes.map((c) => Number(c.id)));
  const linha = (p) => (
    <ListItem key={p.id} divider secondaryAction={
      ligados.has(p.id)
        ? <Chip size="small" color="primary" label="Ligado" />
        : <Button size="small" variant="outlined" disabled={ocupado} onClick={() => ligar(p)}>Ligar</Button>
    }>
      <ListItemText
        primary={p.nome || `#${p.id}`}
        secondary={[p.documento, (p.telefones || []).join(" · "), p.cidade, p.via].filter(Boolean).join(" — ")}
      />
    </ListItem>
  );

  return (
    <Dialog open={aberto} onClose={fechar} fullWidth maxWidth="sm">
      <DialogTitle>Quem é este contato no {rotulo}?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" gutterBottom>Ligado a</Typography>
        {clientes.length === 0 && <Typography variant="body2" color="text.secondary" gutterBottom>Nenhum cliente ainda. Um contato pode ser ligado a mais de um cliente (ex.: empresas do mesmo grupo).</Typography>}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {clientes.map((c) => <Chip key={c.id} label={c.nome || `#${c.id}`} onDelete={() => desligar(c)} color="primary" variant="outlined" />)}
        </Box>
        <Typography variant="subtitle2">Pelo número do WhatsApp</Typography>
        {sug === null ? <CircularProgress size={20} sx={{ my: 1 }} /> : sug.length === 0
          ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Nada encontrado pelo número. Pesquise pelo nome ou documento.</Typography>
          : <List dense sx={{ mb: 2 }}>{sug.map(linha)}</List>}
        <form onSubmit={buscar} style={{ display: "flex", gap: 8 }}>
          <TextField id="integracao-busca" size="small" fullWidth label="Nome, razão social, CPF ou CNPJ" value={q} onChange={(e) => setQ(e.target.value)} />
          <Button type="submit" variant="contained" disabled={ocupado || q.trim().length < 3}>Pesquisar</Button>
        </form>
        {res && (res.length === 0
          ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Nenhum cliente encontrado.</Typography>
          : <List dense>{res.map(linha)}</List>)}
      </DialogContent>
      <DialogActions><Button onClick={fechar}>Fechar</Button></DialogActions>
    </Dialog>
  );
}

// Dados do registro só para ver (pedido do Jds): como a tela de edição do
// sistema, mas sem salvar. Os campos vêm prontos do servidor (lista fechada).
function JanelaDetalhe({ alvo, fechar, contactId }) {
  const [dados, setDados] = useState(null);
  useEffect(() => {
    if (!alvo) return;
    setDados(null);
    api.get(`/integracao/contato/${contactId}/detalhe/${alvo.tipo}/${alvo.id}`)
      .then(({ data }) => setDados(data))
      .catch((e) => { avisarErro(e); fechar(); });
  }, [alvo, contactId, fechar]);
  const longo = (c) => c.valor.length > 60 || /Observa|Descri|Endere|Linha/.test(c.rotulo);
  return (
    <Dialog open={!!alvo} onClose={fechar} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {dados ? dados.titulo : "Carregando…"}
        <Chip size="small" label="somente leitura" variant="outlined" />
      </DialogTitle>
      <DialogContent dividers>
        {dados && Array.isArray(dados.itens) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Produtos lançados ({dados.itens.length})
            </Typography>
            {dados.itens.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nenhum produto lançado.</Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Qtd.</TableCell>
                      <TableCell align="right">Valor unit.</TableCell>
                      <TableCell align="right">Desconto</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dados.itens.map((i, n) => (
                      <TableRow key={n}>
                        <TableCell>
                          {i.produto}
                          {i.detalhe && <Typography variant="caption" display="block" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>{i.detalhe}</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{i.quantidade}{i.unidade ? ` ${i.unidade}` : ""}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{i.unitario}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{i.desconto}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{i.total}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} align="right" sx={{ fontWeight: 600 }}>Total dos produtos</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{dados.totalItens}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
        {!dados ? <CircularProgress size={24} /> : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, pt: 1 }}>
            {dados.campos.map((c) => (
              <TextField key={c.rotulo} label={c.rotulo} value={c.valor} size="small" fullWidth
                multiline={longo(c)} maxRows={6}
                InputProps={{ readOnly: true }} sx={longo(c) ? { gridColumn: { sm: "1 / -1" } } : undefined} />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={fechar}>Fechar</Button></DialogActions>
    </Dialog>
  );
}

function JanelaFinanceiro({ aberto, fechar, contactId, ticketId, idContrato, titulo, verDetalhe }) {
  const [dados, setDados] = useState(null);
  const [menu, setMenu] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setDados(null);
    api.get(`/integracao/contato/${contactId}/financeiro`, { params: idContrato ? { idContrato } : {} })
      .then(({ data }) => setDados(data))
      .catch((e) => { avisarErro(e); fechar(); });
  }, [aberto, contactId, idContrato, fechar]);

  const enviarBoleto = async (l) => {
    setMenu(null); setEnviando(true);
    try { await api.post(`/integracao/contato/${contactId}/boleto/${l.id}`, { ticketId }); toast.success("Boleto enviado na conversa."); }
    catch (err) { avisarErro(err); }
    setEnviando(false);
  };
  const enviarNota = async (l) => {
    setMenu(null); setEnviando(true);
    try { await api.post(`/integracao/contato/${contactId}/nfse/${l.id}`, { ticketId }); toast.success("Nota fiscal enviada na conversa."); }
    catch (err) { avisarErro(err); }
    setEnviando(false);
  };
  const linkNota = async (l) => {
    setMenu(null);
    try {
      const { data } = await api.get(`/integracao/contato/${contactId}/nfse/${l.id}`);
      if (data.link) copiar(data.link, "Link da NFS-e copiado."); else toast.info("Esta conta ainda não tem NFS-e.");
    } catch (err) { avisarErro(err); }
  };

  const l = menu && menu.linha;
  return (
    <Dialog open={aberto} onClose={fechar} fullWidth maxWidth="md">
      <DialogTitle>{titulo || "Financeiro · em aberto"}</DialogTitle>
      <DialogContent dividers sx={{ overflowX: "auto" }}>
        {!dados ? <CircularProgress size={24} /> : dados.linhas.length === 0
          ? <Typography color="text.secondary">Nenhuma conta em aberto.</Typography>
          : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Vencimento</TableCell><TableCell>Cliente</TableCell><TableCell>Descrição</TableCell>
                <TableCell align="right">Valor</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {dados.linhas.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.vencimento}</TableCell>
                    <TableCell>{r.cliente}{!r.idContrato && <Chip size="small" label="sem contrato" sx={{ ml: 1 }} />}</TableCell>
                    <TableCell>{r.descricao}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{valorBR(r.valor)}</TableCell>
                    <TableCell padding="checkbox">
                      <Tooltip title="Tarefas"><IconButton size="small" onClick={(e) => setMenu({ el: e.currentTarget, linha: r })}><MoreVertIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        <Menu anchorEl={menu && menu.el} open={!!menu} onClose={() => setMenu(null)}>
          {l && <MenuItem onClick={() => { setMenu(null); verDetalhe({ tipo: "conta", id: l.id }); }}>Ver dados da conta</MenuItem>}
          {l && l.podeBoleto && <MenuItem disabled={enviando} onClick={() => enviarBoleto(l)}>Enviar boleto nesta conversa (PDF)</MenuItem>}
          {l && dados && dados.nfse && <MenuItem disabled={enviando} onClick={() => enviarNota(l)}>Enviar nota fiscal nesta conversa (PDF)</MenuItem>}
          {l && l.linhaDigitavel && <MenuItem onClick={() => { setMenu(null); copiar(l.linhaDigitavel, "Linha digitável copiada."); }}>Copiar linha digitável</MenuItem>}
          {l && dados && dados.nfse && <MenuItem onClick={() => linkNota(l)}>Copiar link da NFS-e</MenuItem>}
        </Menu>
      </DialogContent>
      <DialogActions>{enviando && <CircularProgress size={18} sx={{ mr: 1 }} />}<Button onClick={fechar}>Fechar</Button></DialogActions>
    </Dialog>
  );
}

function JanelaContratos({ aberto, fechar, contactId, ticketId, abrirFinanceiro, verDetalhe }) {
  const [dados, setDados] = useState(null);
  const [menu, setMenu] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setDados(null);
    api.get(`/integracao/contato/${contactId}/contratos`)
      .then(({ data }) => setDados(data))
      .catch((e) => { avisarErro(e); fechar(); });
  }, [aberto, contactId, fechar]);

  const enviar = async (c) => {
    setMenu(null); setEnviando(true);
    try { await api.post(`/integracao/contato/${contactId}/contrato/${c.id}`, { ticketId }); toast.success("Contrato enviado na conversa."); }
    catch (err) { avisarErro(err); }
    setEnviando(false);
  };

  const c = menu && menu.linha;
  return (
    <Dialog open={aberto} onClose={fechar} fullWidth maxWidth="md">
      <DialogTitle>Contratos</DialogTitle>
      <DialogContent dividers sx={{ overflowX: "auto" }}>
        {!dados ? <CircularProgress size={24} /> : dados.linhas.length === 0
          ? <Typography color="text.secondary">Nenhum contrato para os clientes ligados.</Typography>
          : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Contrato</TableCell><TableCell>Cliente</TableCell><TableCell>Título</TableCell>
                <TableCell align="right">Valor</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {dados.linhas.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{r.codigo || r.id}{r.proposta && <Chip size="small" label="proposta" sx={{ ml: 1 }} />}</TableCell>
                    <TableCell>{r.cliente}</TableCell>
                    <TableCell>{r.titulo}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{valorBR(r.valor)}</TableCell>
                    <TableCell padding="checkbox">
                      <Tooltip title="Tarefas"><IconButton size="small" onClick={(e) => setMenu({ el: e.currentTarget, linha: r })}><MoreVertIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        <Menu anchorEl={menu && menu.el} open={!!menu} onClose={() => setMenu(null)}>
          {c && <MenuItem onClick={() => { setMenu(null); verDetalhe({ tipo: "contrato", id: c.id }); }}>Ver dados do contrato</MenuItem>}
          {c && dados && dados.financeiro && <MenuItem onClick={() => { setMenu(null); abrirFinanceiro(c); }}>Ver financeiro deste contrato</MenuItem>}
          {c && dados && dados.impressao && <MenuItem disabled={enviando} onClick={() => enviar(c)}>Enviar contrato nesta conversa (PDF)</MenuItem>}
        </Menu>
      </DialogContent>
      <DialogActions>{enviando && <CircularProgress size={18} sx={{ mr: 1 }} />}<Button onClick={fechar}>Fechar</Button></DialogActions>
    </Dialog>
  );
}

// Pedidos de venda e NF-e (Jds 02/11): mesma lógica do contrato.
function JanelaLista({ aberto, fechar, contactId, ticketId, verDetalhe, tipo }) {
  const ehPedido = tipo === "pedido";
  const [dados, setDados] = useState(null);
  const [menu, setMenu] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setDados(null);
    api.get(`/integracao/contato/${contactId}/${ehPedido ? "pedidos" : "nfe"}`)
      .then(({ data }) => setDados(data))
      .catch((e) => { avisarErro(e); fechar(); });
  }, [aberto, contactId, fechar, ehPedido]);

  const enviar = async (r) => {
    setMenu(null); setEnviando(true);
    try {
      await api.post(`/integracao/contato/${contactId}/${ehPedido ? "pedido" : "nfe"}/${r.id}`, { ticketId });
      toast.success(ehPedido ? "Pedido enviado na conversa." : "DANFE enviado na conversa.");
    } catch (err) { avisarErro(err); }
    setEnviando(false);
  };

  const r = menu && menu.linha;
  return (
    <Dialog open={aberto} onClose={fechar} fullWidth maxWidth="md">
      <DialogTitle>{ehPedido ? "Pedidos de venda" : "Notas fiscais (NF-e)"}</DialogTitle>
      <DialogContent dividers sx={{ overflowX: "auto" }}>
        {!dados ? <CircularProgress size={24} /> : dados.linhas.length === 0
          ? <Typography color="text.secondary">{ehPedido ? "Nenhum pedido para os clientes ligados." : "Nenhuma NF-e para os clientes ligados."}</Typography>
          : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{ehPedido ? "Pedido" : "Nota"}</TableCell><TableCell>{ehPedido ? "Data" : "Emissão"}</TableCell>
                <TableCell>Cliente</TableCell><TableCell>Situação</TableCell>
                <TableCell align="right">Valor</TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>
                {dados.linhas.map((l) => (
                  <TableRow key={l.id} hover>
                    <TableCell sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                      {ehPedido ? l.numero : (l.numero || "sem número")}{!ehPedido && l.modelo === "65" && <Chip size="small" label="NFC-e" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{ehPedido ? l.data : l.emissao}</TableCell>
                    <TableCell>{l.cliente}</TableCell>
                    <TableCell>{l.situacao}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{l.valor ? valorBR(l.valor) : ""}</TableCell>
                    <TableCell padding="checkbox">
                      <Tooltip title="Tarefas"><IconButton size="small" onClick={(e) => setMenu({ el: e.currentTarget, linha: l })}><MoreVertIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        <Menu anchorEl={menu && menu.el} open={!!menu} onClose={() => setMenu(null)}>
          {r && <MenuItem onClick={() => { setMenu(null); verDetalhe({ tipo: ehPedido ? "pedido" : "nfe", id: r.id }); }}>{ehPedido ? "Ver dados do pedido" : "Ver dados da nota"}</MenuItem>}
          {r && ehPedido && dados && dados.impressao && <MenuItem disabled={enviando} onClick={() => enviar(r)}>Enviar pedido nesta conversa (PDF)</MenuItem>}
          {r && !ehPedido && <MenuItem disabled={enviando || !r.autorizada} onClick={() => enviar(r)}>{r.autorizada ? "Enviar DANFE nesta conversa (PDF)" : "DANFE: nota ainda não autorizada"}</MenuItem>}
        </Menu>
      </DialogContent>
      <DialogActions>{enviando && <CircularProgress size={18} sx={{ mr: 1 }} />}<Button onClick={fechar}>Fechar</Button></DialogActions>
    </Dialog>
  );
}

// Novo contato no Sabe Gestão a partir da conversa (Jds 02/11). Única tela que
// GRAVA no sistema: cliente ligado, tipo/status das listas do sistema, descrição.
// A descrição já vem com o texto que o atendente tinha selecionado na conversa.
function JanelaNovoContato({ aberto, fechar, contactId, nomeContato, textoInicial }) {
  const [op, setOp] = useState(null);
  const [f, setF] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setOp(null);
    api.get(`/integracao/contato/${contactId}/novo-contato`)
      .then(({ data }) => {
        setOp(data);
        const wa = (data.tipos || []).find((t) => /whats/i.test(t.nome));
        setF({ idPessoa: data.clientes[0] ? data.clientes[0].id : "", idTipo: wa ? wa.id : "", idStatus: "", pessoaContato: nomeContato || "", descricao: textoInicial || "" });
      })
      .catch((e) => { avisarErro(e); fechar(); });
  }, [aberto, contactId, fechar, nomeContato, textoInicial]);

  const salvar = async () => {
    if (!f.descricao || !f.descricao.trim()) { toast.error(MENSAGENS.ERR_INTEGRACAO_DESCRICAO); return; }
    setSalvando(true);
    try {
      const { data } = await api.post(`/integracao/contato/${contactId}/novo-contato`, f);
      toast.success(`Contato ${data.idContato} incluído no sistema.`);
      fechar();
    } catch (err) { avisarErro(err); }
    setSalvando(false);
  };

  const campo = (k) => ({ value: f[k] === undefined ? "" : f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }) });
  return (
    <Dialog open={aberto} onClose={salvando ? undefined : fechar} fullWidth maxWidth="sm">
      <DialogTitle>Novo contato no sistema</DialogTitle>
      <DialogContent dividers>
        {!op ? <CircularProgress size={24} /> : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField select size="small" label="Cliente" {...campo("idPessoa")} sx={{ gridColumn: "1 / -1" }}>
              {op.clientes.map((c) => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Tipo de contato" required {...campo("idTipo")}>
              {op.tipos.map((t) => <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Status" {...campo("idStatus")}>
              <MenuItem value="">— sem status —</MenuItem>
              {op.status.map((t) => <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Pessoa de contato" {...campo("pessoaContato")} inputProps={{ maxLength: 100 }} sx={{ gridColumn: "1 / -1" }} />
            <TextField size="small" label="Descrição" required multiline minRows={5} {...campo("descricao")} inputProps={{ maxLength: 8000 }}
              helperText="Dica: selecione um trecho da conversa antes de abrir Tarefas e ele já vem aqui." sx={{ gridColumn: "1 / -1" }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {salvando && <CircularProgress size={18} sx={{ mr: 1 }} />}
        <Button onClick={fechar} disabled={salvando}>Cancelar</Button>
        <Button variant="contained" onClick={salvar} disabled={!op || salvando || !f.idPessoa || !f.idTipo}>Incluir no sistema</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function IntegracaoSistema({ contact, ticket }) {
  const [estado, setEstado] = useState(null);
  const [janela, setJanela] = useState(null); // "ligar" | "fin" | "ctr"
  const [contrato, setContrato] = useState(null);
  const [tarefas, setTarefas] = useState(null); // âncora do menu Tarefas
  const [detalhe, setDetalhe] = useState(null); // { tipo, id }
  const [selecao, setSelecao] = useState(""); // texto selecionado na conversa ao abrir Tarefas
  const contactId = contact && contact.id;

  useEffect(() => {
    setEstado(null);
    if (!contactId || (ticket && ticket.isGroup)) return;
    let vivo = true;
    api.get(`/integracao/contato/${contactId}`)
      .then(({ data }) => vivo && setEstado(data))
      .catch(() => vivo && setEstado(null));
    return () => { vivo = false; };
  }, [contactId, ticket && ticket.isGroup]);

  const fechar = useCallback(() => { setJanela(null); setContrato(null); }, []);
  const fecharDetalhe = useCallback(() => setDetalhe(null), []);

  // Sem integração, sem usuário ligado ou sem acesso ao cadastro: não aparece nada.
  if (!estado || !estado.ativo || estado.bloqueado || !estado.janelas || !estado.janelas.cadastro) return null;
  const { janelas, rotulo } = estado;
  const clientes = estado.clientes || [];
  const ligado = clientes.length > 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, mr: 1 }} onClick={(e) => e.stopPropagation()}>
      {ligado ? (
        <Tooltip title={`${rotulo}: ${clientes.map((c) => c.nome).join(", ")} — clique para ajustar`}>
          <Chip
            size="small" color="info" variant="outlined" icon={<LinkIcon />}
            label={`${clientes[0].nome}${clientes.length > 1 ? ` +${clientes.length - 1}` : ""}`}
            onClick={() => setJanela("ligar")}
            sx={{ maxWidth: 360 }}
          />
        </Tooltip>
      ) : (
        <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={() => setJanela("ligar")} sx={{ whiteSpace: "nowrap" }}>
          Ligar ao {rotulo}
        </Button>
      )}
      {ligado && (
        <Tooltip title="Tarefas"><IconButton size="small" color="primary" onMouseDown={() => { try { setSelecao(String(window.getSelection() || "").trim().slice(0, 8000)); } catch (e) { setSelecao(""); } }} onClick={(e) => setTarefas(e.currentTarget)}><AssignmentOutlinedIcon fontSize="small" /></IconButton></Tooltip>
      )}
      <Menu anchorEl={tarefas} open={!!tarefas} onClose={() => setTarefas(null)}>
        {janelas.financeiro && <MenuItem onClick={() => { setTarefas(null); setJanela("fin"); }}><ListItemIcon><PaidOutlinedIcon fontSize="small" /></ListItemIcon>Financeiro em aberto</MenuItem>}
        {janelas.contratos && <MenuItem onClick={() => { setTarefas(null); setJanela("ctr"); }}><ListItemIcon><DescriptionOutlinedIcon fontSize="small" /></ListItemIcon>Contratos</MenuItem>}
        {janelas.pedidos && <MenuItem onClick={() => { setTarefas(null); setJanela("ped"); }}><ListItemIcon><ShoppingCartOutlinedIcon fontSize="small" /></ListItemIcon>Pedidos de venda</MenuItem>}
        {janelas.nfe && <MenuItem onClick={() => { setTarefas(null); setJanela("nfe"); }}><ListItemIcon><ReceiptLongOutlinedIcon fontSize="small" /></ListItemIcon>Notas fiscais (NF-e)</MenuItem>}
        {janelas.incluirContato && <MenuItem onClick={() => { setTarefas(null); setJanela("novo"); }}><ListItemIcon><AddCommentOutlinedIcon fontSize="small" /></ListItemIcon>Novo contato no sistema</MenuItem>}
        {clientes.map((c) => (
          <MenuItem key={c.id} onClick={() => { setTarefas(null); setDetalhe({ tipo: "cliente", id: c.id }); }}>
            <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
            {clientes.length > 1 ? `Ver cadastro: ${c.nome}` : "Ver cadastro do cliente"}
          </MenuItem>
        ))}
      </Menu>

      <JanelaLigar aberto={janela === "ligar"} fechar={fechar} contactId={contactId} rotulo={rotulo}
        clientes={clientes} onMudou={(lista) => setEstado((s) => ({ ...s, clientes: lista }))} />
      <JanelaFinanceiro aberto={janela === "fin"} fechar={fechar} contactId={contactId} ticketId={ticket && ticket.id}
        idContrato={contrato && contrato.id}
        titulo={contrato ? `Financeiro · contrato ${contrato.codigo || contrato.id} (${contrato.cliente})` : null}
        verDetalhe={setDetalhe} />
      <JanelaContratos aberto={janela === "ctr"} fechar={fechar} contactId={contactId} ticketId={ticket && ticket.id}
        abrirFinanceiro={(c) => { setContrato(c); setJanela("fin"); }} verDetalhe={setDetalhe} />
      <JanelaLista aberto={janela === "ped"} tipo="pedido" fechar={fechar} contactId={contactId} ticketId={ticket && ticket.id} verDetalhe={setDetalhe} />
      <JanelaLista aberto={janela === "nfe"} tipo="nfe" fechar={fechar} contactId={contactId} ticketId={ticket && ticket.id} verDetalhe={setDetalhe} />
      <JanelaNovoContato aberto={janela === "novo"} fechar={fechar} contactId={contactId} nomeContato={contact && contact.name} textoInicial={selecao} />
      <JanelaDetalhe alvo={detalhe} fechar={fecharDetalhe} contactId={contactId} />
    </Box>
  );
}

